/* eslint-disable @typescript-eslint/no-use-before-define */
import { useApolloClient } from '@apollo/client';
import classNames from 'classnames/bind';
import {
  DirectionType,
  IChartBarData,
  IChartOptions,
  IDailyPrice,
  LoadingStatus,
  TimeCategory,
  areaCode,
  getCallItem,
  getInitChartDataList,
  getInitChartStatus,
  getPcToolTipBoxLimitIndex,
  getPullBarList,
  getSelectedBarList,
  getSelectedBarOrderIndex,
  mobileDeviceWidth,
  setChartDataListDomestic,
  setChartStateLoading,
  useWindowWidth,
} from 'common/modal/chart/utilChart';
import { DateFormat } from 'common/types';
import { days } from 'common/utils';
import { DeviceContext, DeviceType } from 'contexts/device';
import { useContext, useEffect, useRef, useState } from 'react';
import styles from './chartBar.module.scss';
import GET_FLIGHT_FARE from './flightFare.gql';
import PriceBox from './priceBox';

const cx = classNames.bind(styles);

const barTotalLength = 365; // bar 1년개수
const barDefaultHeight = 5; // bar 기본 높이
const barLastShowCount = 3; // 끝에 3개만 표시
const barWidth = 12;
const barMargin = 4; // bar margin 2 + 2
const barTotalWidth = barWidth + barMargin;
const CALL_DATE_LENGTH = 55;
const barMobileGuideIndex = 2;
const defaultChartOptions = {
  barShowLength: 0,
  dataList: [],
  maxPrice: 0,
  smxp: 0,
};
const chartStartDateOffset = 0;

const ChartBar = ({
  trip,
  isDeparture,
  departureTimeList,
  applyDepartureStartDate,
  applyMouseOverDate,
  departureDate,
  departureCity,
  arrivalCity,
}: any) => {
  const device = useContext(DeviceContext);
  const [currentDevice, setCurrentDevice] = useState<DeviceType>(device);
  const windowWidth = useWindowWidth(200);
  const pcToolTipBoxLimitIndex = useRef(0);
  const chartStatus = useRef(
    getInitChartStatus(
      departureDate,
      barTotalLength - barLastShowCount,
      DirectionType.RIGHT,
      chartStartDateOffset,
    ),
  );
  const barBox = useRef<HTMLDivElement>(null);
  const chartOptions = useRef<IChartOptions>(defaultChartOptions);
  const [barDataList, setBarDataList] = useState<IChartBarData[]>([]); // {idx: 0, price: null, date: new Date(), dateStr: '20211206', state: -2}
  const [mouseOverItem, setMouseOverItem] = useState<IChartBarData | null>(
    null,
  );
  const [selectedBarItem, setSelectedBarItem] = useState<IChartBarData | null>(
    null,
  ); // pc 선택된 bar item
  const [selectedBarIndex, setSelectedBarIndex] = useState<number | null>(null); // pc 선택된 bar index
  const apolloClient = useApolloClient();

  const getChartPrice = (
    _chartOptions: any,
    isCallDate: Date,
    idx: number,
    _direction: DirectionType,
  ) => {
    const { dataList } = _chartOptions.current;
    const callItem = getCallItem(
      _chartOptions,
      CALL_DATE_LENGTH,
      isCallDate,
      idx,
      _direction,
    );
    setChartStateLoading(dataList, callItem.idx, CALL_DATE_LENGTH);
    callChartApi(callItem, chartOptions);
  };

  const callChartApi = async (callItem: any, _chartOptions: any) => {
    const startDate = callItem.start || new Date();
    const searchDateList = [...Array(CALL_DATE_LENGTH)].map((item, idx) =>
      days(startDate).add(idx, 'day').format(DateFormat.YMD),
    );

    const result = await apolloClient.query({
      query: GET_FLIGHT_FARE,
      variables: {
        param: {
          departureCityCode: departureCity.code,
          arrivalCityCode: arrivalCity.code,
          timeCategoryList:
            departureTimeList.length === 6 // 시간 모두 선택시에는 DAY 로만.
              ? [TimeCategory.DAY]
              : departureTimeList,
          searchDateList,
        },
      },
    });

    const chartList: IDailyPrice[] = result?.data?.domesticDailyPriceList || [];

    const dailyPriceMap = new Map(chartList.map(item => [item.date, item]));

    setChartDataListDomestic(
      dailyPriceMap,
      CALL_DATE_LENGTH,
      callItem,
      _chartOptions,
    );
    const { pullBarList }: any = getPullBarList(
      _chartOptions,
      chartStatus.current.startIndex,
    );
    setBarDataList(pullBarList);
  };

  // touch start
  const touchStartHandler = (e: any) => {
    if (currentDevice === DeviceType.PC) {
      return;
    }
    chartOptions.current.smxp = e.touches?.['0']?.pageX || 0;
  };

  // touch move
  const touchMoveHandler = (e: any) => {
    if (currentDevice === DeviceType.PC) return;
    let currentIndex = chartStatus.current.startIndex;
    const { smxp, dataList } = chartOptions.current;
    const cmxp = e.touches?.['0']?.pageX || 0;
    const diff = cmxp - smxp;
    if (Math.abs(diff) >= barTotalWidth) {
      const moveNum =
        Math.floor(Math.abs(diff) / barTotalWidth) * (diff < 0 ? -1 : 1);
      currentIndex += -moveNum;
      // 처음 인덱스는 0
      if (currentIndex < 0) {
        currentIndex = 0;
      }
      // 마지막엔 3개만 보이기때문에
      if (currentIndex > dataList.length - barLastShowCount) {
        currentIndex = dataList.length - barLastShowCount;
      }
      const dir = moveNum > 0 ? DirectionType.LEFT : DirectionType.RIGHT;
      const { pullBarList } = getPullBarList(chartOptions, currentIndex);
      chartOptions.current.smxp = smxp + moveNum * barTotalWidth;
      chartStatus.current = { startIndex: currentIndex, direction: dir };
      setBarDataList(pullBarList);
      // 날짜 설정
      applyDepartureStartDate(
        isDeparture,
        dataList[currentIndex + barMobileGuideIndex].date,
      );
      // 선택된 bar
      if (selectedBarItem) {
        const changeIndex = currentIndex + barMobileGuideIndex;
        const barItem = dataList[changeIndex];
        setSelectedBarItem(barItem);
        setSelectedBarIndex(changeIndex);
        setMouseOverItem(null);
      }
    }
  };

  // pc 다음, 이전 처리
  const nextPage = (dir: DirectionType) => {
    if (currentDevice === DeviceType.MOBILE) return;
    const { dataList, barShowLength } = chartOptions.current;
    let currentIndex = chartStatus.current.startIndex;
    if (dir === DirectionType.RIGHT) {
      currentIndex += barShowLength;
      if (currentIndex >= dataList.length - barLastShowCount)
        currentIndex = dataList.length - barLastShowCount;
    }
    if (dir === DirectionType.LEFT) {
      currentIndex -= barShowLength;
      if (currentIndex < 0) currentIndex = 0;
    }
    const { pullBarList } = getPullBarList(chartOptions, currentIndex);
    chartStatus.current = { startIndex: currentIndex, direction: dir };
    setBarDataList(pullBarList);
  };

  // pc 마우스 over
  const mouseOverHandler = (item: IChartBarData) => {
    if (currentDevice === DeviceType.MOBILE || item.idx < barMobileGuideIndex)
      return;
    setMouseOverItem(item);
    applyMouseOverDate(isDeparture, item.date);
  };
  // pc 마우스 out
  const mouseOutHandler = () => {
    if (currentDevice === DeviceType.MOBILE) return;
    setMouseOverItem(null);
    applyMouseOverDate(isDeparture, null);
  };
  // pc 마우스 click
  const clickBarHandler = (item: IChartBarData) => {
    if (currentDevice === DeviceType.MOBILE || item.idx < barMobileGuideIndex)
      return;
    setSelectedBarItem(item);
    setSelectedBarIndex(item.idx);
    applyDepartureStartDate(isDeparture, item.date);
  };

  // pc 선택된 bar 가 페이지에 있는지 여부
  const getShowBarLength = () => {
    if (!barBox.current) return 0;
    return Math.floor((barBox.current?.offsetWidth - 10) / barTotalWidth); // 10px정도 줄여서 bar 개수를 맞춘다
  };

  // resize 처리
  useEffect(() => {
    // m -> pc
    if (window && window.innerWidth > mobileDeviceWidth) {
      if (currentDevice !== DeviceType.PC) {
        setCurrentDevice(DeviceType.PC);
      }
    }
    // pc -> m
    if (window && window.innerWidth <= mobileDeviceWidth) {
      if (currentDevice !== DeviceType.MOBILE) {
        setCurrentDevice(DeviceType.MOBILE);
        if (selectedBarItem) {
          // 선택바 있을시 시작은 선택바.idx - 2
          chartStatus.current.startIndex =
            selectedBarItem.idx - barMobileGuideIndex;
        }
      }
    }
    // 가격툴팁 위치 한도 설정
    pcToolTipBoxLimitIndex.current = getPcToolTipBoxLimitIndex(
      barBox?.current?.offsetWidth || 0,
      barTotalWidth,
    );

    if (!barBox.current) return;
    chartOptions.current = {
      ...chartOptions.current,
      barShowLength: getShowBarLength(),
    };
    const { pullBarList, isCallDate, idx } = getPullBarList(
      chartOptions,
      chartStatus.current.startIndex,
    );
    if (isCallDate) {
      getChartPrice(
        chartOptions,
        isCallDate,
        idx,
        chartStatus.current.direction,
      );
    }
    setBarDataList(pullBarList);
  }, [windowWidth]);

  // index 변경시
  useEffect(() => {
    const { startIndex: currentIndex, direction: dir } = chartStatus.current;
    const { isCallDate, idx } = getPullBarList(chartOptions, currentIndex, dir);
    if (isCallDate) {
      getChartPrice(chartOptions, isCallDate, idx, dir);
    }
  }, [chartStatus.current.startIndex]);

  // 상품 변경시
  useEffect(() => {
    chartOptions.current = {
      barShowLength: getShowBarLength(),
      dataList: getInitChartDataList(
        days(days().format(DateFormat.YMD)).subtract(2, 'day').toDate(),
        barTotalLength,
      ),
      maxPrice: 0,
      smxp: 0,
    };
    // 모든곳조회 상품 클릭시
    chartStatus.current = getInitChartStatus(
      departureDate,
      barTotalLength - barLastShowCount,
      DirectionType.RIGHT,
      chartStartDateOffset,
    );
    const { startIndex } = chartStatus.current;
    const { pullBarList, isCallDate, idx }: any = getPullBarList(
      chartOptions,
      startIndex,
    );
    getChartPrice(chartOptions, isCallDate, idx, DirectionType.RIGHT);
    setBarDataList(pullBarList);

    const changeIndex = startIndex + barMobileGuideIndex;
    const barItem = chartOptions.current.dataList[changeIndex];
    setSelectedBarItem(barItem);
    setSelectedBarIndex(changeIndex);
    applyDepartureStartDate(
      isDeparture,
      chartOptions.current.dataList[changeIndex].date,
    );
  }, [departureTimeList, departureCity, arrivalCity]);

  // init device, resize
  useEffect(() => {
    if (window) {
      setCurrentDevice(
        window.innerWidth > mobileDeviceWidth
          ? DeviceType.PC
          : DeviceType.MOBILE,
      );
    }
  }, []);

  // 선택된 bar list
  const selectedBarList = getSelectedBarList(
    currentDevice === DeviceType.MOBILE,
    barDataList,
    1,
    selectedBarIndex,
  );

  return (
    <div className={cx('chartBar')}>
      <div
        className={cx('graph')}
        data-event-nlog="click"
        data-event-area-code={`${areaCode}.domgraphmove`}
        onTouchStart={touchStartHandler}
        onTouchMove={touchMoveHandler}
      >
        <div>
          <i className={cx('index', 'as_max')}>
            <b className={cx('point')}>
              {chartOptions.current.maxPrice === 0
                ? ''
                : `${(chartOptions.current.maxPrice / 10000).toFixed(1)}만원`}
            </b>
          </i>
          <i className={cx('index', 'as_current')}>
            <b className={cx('point')}>
              {chartOptions.current.maxPrice === 0
                ? ''
                : `${(chartOptions.current.maxPrice / 10000 / 2).toFixed(
                    1,
                  )}만원`}
            </b>
          </i>
          <i className={cx('index', 'as_min')}>
            <b className={cx('point')}>0원</b>
          </i>
        </div>

        <div
          className={cx('bar_items')}
          ref={barBox}
          onMouseOut={mouseOutHandler}
          onMouseLeave={mouseOutHandler}
        >
          {barDataList.map((item: any, i: number) => {
            const { maxPrice } = chartOptions.current;
            const percent = (100 * item.price) / maxPrice;
            const barHeight =
              item.price === null
                ? `${barDefaultHeight}%`
                : `${percent < barDefaultHeight ? barDefaultHeight : percent}%`; // bar height
            const barSelected = selectedBarList.find(n => item.idx === n);

            return (
              <div
                className={cx('bar_item')}
                key={i}
                onMouseOver={() => {
                  mouseOverHandler(item);
                }}
                onClick={() => {
                  clickBarHandler(item);
                }}
              >
                <div
                  className={cx('bar')}
                  style={{ height: barHeight }}
                  aria-selected={!!barSelected}
                  aria-disabled={
                    item.state <= LoadingStatus.LOADING || item.price === null
                  }
                >
                  {/* <span style={{ fontSize: '3px' }}>{item.idx}</span> */}
                </div>
                {item.date.getDate() === 1 && (
                  <i className={cx('month')}>
                    <b className={cx('month_value')}>
                      {item.date.getMonth() + 1}월
                    </b>
                  </i>
                )}
              </div>
            );
          })}
        </div>

        {/* 가격 */}
        <PriceBox
          device={currentDevice}
          trip={trip}
          barDataList={barDataList}
          barTotalWidth={barTotalWidth}
          mouseOverItem={mouseOverItem}
          selectedBarItem={selectedBarItem}
          pcToolTipBoxLimitIndex={pcToolTipBoxLimitIndex.current}
          selectedBarOrderIndex={getSelectedBarOrderIndex(
            mouseOverItem || selectedBarItem,
            barDataList,
          )}
        />
      </div>

      <div className={cx('btns')}>
        <button
          className={cx('left')}
          type="button"
          data-event-nlog="click"
          data-event-area-code={`${areaCode}.domgraphmove`}
          onClick={() => {
            nextPage(DirectionType.LEFT);
          }}
          title="이전"
        />
        <button
          className={cx('right')}
          type="button"
          data-event-nlog="click"
          data-event-area-code={`${areaCode}.domgraphmove`}
          onClick={() => {
            nextPage(DirectionType.RIGHT);
          }}
          title="다음"
        />
      </div>
    </div>
  );
};

export default ChartBar;
